// The floor plan's rules — the ones arrange mode and the server both run (spec/13 §3.4, F-ORG-03).
//
// "Validation is the same on the server." It is literally the same: the console calls
// `floorPlanProblems` before it lets a manager save, and `PUT …/floor-plan` calls it again before it
// writes, so a plan the console accepted is never refused by the server for a rule the console did
// not know, and a plan crafted by hand is refused with the message the console would have shown.
//
// The rules, and nothing more — the spec names these and no others:
//
//   - the grid is 1–60 columns by 1–100 rows;
//   - every rectangle lies inside the grid;
//   - at most 30 zones and 100 fixtures on a floor;
//   - zones do not overlap one another (a fixture may sit inside a zone: the counter is in the
//     lounge), and a zone is drawn once;
//   - stations (F-STN-19) lie inside the grid, never share a square with another station, and a
//     station is placed once. A station may sit inside a zone and next to a fixture.
//
// What a zone *is* — that it exists at this venue, on this floor — is the server's to check
// against its own rows; `zoneFloorProblems` does the arithmetic once the server has read them.
// Pure: no clock, no I/O, deterministic order.
import { FLOOR_MAX_COLUMNS, FLOOR_MAX_ROWS, MAX_FIXTURES_PER_FLOOR, MAX_ZONES_PER_FLOOR, } from '@clavian/contracts/constants';
/** The messages, once, so the zone endpoint and the plan say the same thing about the same rule. */
export const FLOOR_PLAN_MESSAGES = {
    columns: `A floor is 1 to ${FLOOR_MAX_COLUMNS} columns wide.`,
    rows: `A floor is 1 to ${FLOOR_MAX_ROWS} rows deep.`,
    tooManyZones: `A floor holds at most ${MAX_ZONES_PER_FLOOR} zones.`,
    tooManyFixtures: `A floor holds at most ${MAX_FIXTURES_PER_FLOOR} fixtures.`,
    outsideGrid: 'This goes past the edge of the floor. Make the floor bigger or move it in.',
    overlap: (other) => `Zones can’t overlap: this one covers squares of zone ${other + 1}.`,
    duplicate: 'A zone is drawn once. Remove the second rectangle.',
    wrongFloor: (floor) => `This zone belongs to floor ${floor}. Draw it on that floor.`,
    unknownZone: 'There is no such zone at this venue.',
    stationOverlap: (other) => `Two stations can’t share a square: this one covers station ${other + 1}'s.`,
    stationDuplicate: 'A station is placed once. Remove the second tile.',
    unknownStation: 'There is no such station at this venue, or it is the counter PC, which is never on the floor.',
    stationElsewhere: (floor) => `This station is on floor ${floor}. Take it off that floor first.`,
};
/** Do two rectangles share at least one cell? Edges touching is not sharing. */
export function rectsOverlap(a, b) {
    return a.x < b.x + b.w && b.x < a.x + a.w && a.y < b.y + b.h && b.y < a.y + a.h;
}
function insideGrid(rect, columns, rows) {
    return rect.x >= 0 && rect.y >= 0 && rect.w >= 1 && rect.h >= 1 && rect.x + rect.w <= columns && rect.y + rect.h <= rows;
}
/**
 * Every rule a plan breaks, in a stable order (grid, counts, then item by item). Empty means the
 * plan may be saved.
 */
export function floorPlanProblems(plan) {
    const problems = [];
    const columnsOk = Number.isInteger(plan.columns) && plan.columns >= 1 && plan.columns <= FLOOR_MAX_COLUMNS;
    const rowsOk = Number.isInteger(plan.rows) && plan.rows >= 1 && plan.rows <= FLOOR_MAX_ROWS;
    if (!columnsOk)
        problems.push({ field: 'columns', code: 'out_of_range', message: FLOOR_PLAN_MESSAGES.columns });
    if (!rowsOk)
        problems.push({ field: 'rows', code: 'out_of_range', message: FLOOR_PLAN_MESSAGES.rows });
    if (plan.zones.length > MAX_ZONES_PER_FLOOR) {
        problems.push({ field: 'zones', code: 'too_many', message: FLOOR_PLAN_MESSAGES.tooManyZones });
    }
    if (plan.fixtures.length > MAX_FIXTURES_PER_FLOOR) {
        problems.push({ field: 'fixtures', code: 'too_many', message: FLOOR_PLAN_MESSAGES.tooManyFixtures });
    }
    // With a grid that is itself out of range, "inside the grid" has no meaning; say so once.
    const gridKnown = columnsOk && rowsOk;
    const firstIndexOfZone = new Map();
    plan.zones.forEach((zone, index) => {
        const field = `zones.${index}`;
        if (gridKnown && !insideGrid(zone, plan.columns, plan.rows)) {
            problems.push({ field, code: 'outside_grid', message: FLOOR_PLAN_MESSAGES.outsideGrid });
        }
        const first = firstIndexOfZone.get(zone.zoneId);
        if (first !== undefined) {
            problems.push({ field, code: 'duplicate', message: FLOOR_PLAN_MESSAGES.duplicate });
            return;
        }
        firstIndexOfZone.set(zone.zoneId, index);
        // Against every earlier zone, so each overlap is reported once, on the later of the two.
        for (let other = 0; other < index; other += 1) {
            const earlier = plan.zones[other];
            if (earlier !== undefined && earlier.zoneId !== zone.zoneId && rectsOverlap(earlier, zone)) {
                problems.push({ field, code: 'overlap', message: FLOOR_PLAN_MESSAGES.overlap(other) });
                break;
            }
        }
    });
    plan.fixtures.forEach((fixture, index) => {
        if (gridKnown && !insideGrid(fixture, plan.columns, plan.rows)) {
            problems.push({ field: `fixtures.${index}`, code: 'outside_grid', message: FLOOR_PLAN_MESSAGES.outsideGrid });
        }
    });
    const stations = plan.stations ?? [];
    const firstIndexOfStation = new Map();
    stations.forEach((station, index) => {
        const field = `stations.${index}`;
        if (gridKnown && !insideGrid(station, plan.columns, plan.rows)) {
            problems.push({ field, code: 'outside_grid', message: FLOOR_PLAN_MESSAGES.outsideGrid });
        }
        if (firstIndexOfStation.has(station.stationId)) {
            problems.push({ field, code: 'duplicate', message: FLOOR_PLAN_MESSAGES.stationDuplicate });
            return;
        }
        firstIndexOfStation.set(station.stationId, index);
        for (let other = 0; other < index; other += 1) {
            const earlier = stations[other];
            if (earlier !== undefined && earlier.stationId !== station.stationId && rectsOverlap(earlier, station)) {
                problems.push({ field, code: 'overlap', message: FLOOR_PLAN_MESSAGES.stationOverlap(other) });
                break;
            }
        }
    });
    return problems;
}
/**
 * The server's half for stations (F-STN-19): each placed station is one of the venue's floor stations
 * (`placeable` — never a counter PC), and not already on another floor (`elsewhere` maps a station id
 * to the floor it is placed on, for the venue's other floors only).
 */
export function stationPlacementProblems(stations, placeable, elsewhere) {
    const problems = [];
    stations.forEach((station, index) => {
        const field = `stations.${index}`;
        const other = elsewhere.get(station.stationId);
        if (!placeable.has(station.stationId))
            problems.push({ field, code: 'not_found', message: FLOOR_PLAN_MESSAGES.unknownStation });
        else if (other !== undefined)
            problems.push({ field, code: 'wrong_floor', message: FLOOR_PLAN_MESSAGES.stationElsewhere(other) });
    });
    return problems;
}
/**
 * The server's half: each zone on the plan must be one of the venue's zones, on this floor.
 * `zoneFloors` maps a zone id to its floor, for the venue's zones only.
 */
export function zoneFloorProblems(zones, floor, zoneFloors) {
    const problems = [];
    zones.forEach((zone, index) => {
        const field = `zones.${index}`;
        const zoneFloor = zoneFloors.get(zone.zoneId);
        if (zoneFloor === undefined)
            problems.push({ field, code: 'not_found', message: FLOOR_PLAN_MESSAGES.unknownZone });
        else if (zoneFloor !== floor)
            problems.push({ field, code: 'wrong_floor', message: FLOOR_PLAN_MESSAGES.wrongFloor(zoneFloor) });
    });
    return problems;
}
//# sourceMappingURL=floor-plan.js.map